import { httpGet, httpPost, httpPut, httpDelete } from '../lib/http';

// Centralized logger for API calls and errors
function logApiCall(name, params) {
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    console.info(`[API CALL] ${name}`, params);
  }
}

function logApiError(name, params, error) {
  console.error(`[API ERROR] ${name}`, { params, error: error?.message || error });
}

async function safeApiCall(fn, name, params) {
  logApiCall(name, params);
  try {
    const result = await fn();
    return result;
  } catch (error) {
    logApiError(name, params, error);
    throw error;
  }
}

/**
 * Get all study tracks for a user
 */
export async function getUserStudyTracks(userId) {
  return safeApiCall(async () => {
    const res = await httpGet(`/api/users/${encodeURIComponent(userId)}/study-tracks`);
    return await res.json();
  }, 'getUserStudyTracks', { userId });
}

/**
 * Get a specific study track
 */
export async function getStudyTrack(userId, trackId) {
  return safeApiCall(async () => {
    const res = await httpGet(`/api/users/${encodeURIComponent(userId)}/study-tracks/${encodeURIComponent(trackId)}`);
    return await res.json();
  }, 'getStudyTrack', { userId, trackId });
}

/**
 * Create a new study track
 */
export async function createStudyTrack(userId, payload) {
  return safeApiCall(async () => {
    const res = await httpPost(`/api/users/${encodeURIComponent(userId)}/study-tracks`, payload);
    return await res.json();
  }, 'createStudyTrack', { userId, payload });
}

/**
 * Update an existing study track
 */
export async function updateStudyTrack(userId, trackId, payload) {
  return safeApiCall(async () => {
    const res = await httpPut(`/api/users/${encodeURIComponent(userId)}/study-tracks/${encodeURIComponent(trackId)}`, payload);
    return await res.json();
  }, 'updateStudyTrack', { userId, trackId, payload });
}

/**
 * Delete a study track
 */
export async function deleteStudyTrack(userId, trackId) {
  return safeApiCall(async () => {
    const res = await httpDelete(`/api/users/${encodeURIComponent(userId)}/study-tracks/${encodeURIComponent(trackId)}`);
    return await res.json();
  }, 'deleteStudyTrack', { userId, trackId });
}

/**
 * Get user's progress for a specific study track
 */
export async function getTrackProgress(userId, trackId) {
  return safeApiCall(async () => {
    const res = await httpGet(`/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(trackId)}`);
    return await res.json();
  }, 'getTrackProgress', { userId, trackId });
}

/**
 * Mark a topic as complete
 */
export async function markTopicComplete(userId, trackId, topicId) {
  return safeApiCall(async () => {
    const res = await httpPost(
      `/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(trackId)}/topic/${encodeURIComponent(topicId)}/complete`,
      {}
    );
    return await res.json();
  }, 'markTopicComplete', { userId, trackId, topicId });
}

/**
 * Get study notes for a topic
 */
export async function getStudyNotes(userId, pathId, topicId) {
  return safeApiCall(async () => {
    const res = await httpGet(
      `/api/users/${encodeURIComponent(userId)}/study-notes?pathId=${encodeURIComponent(pathId)}&topicId=${encodeURIComponent(topicId)}`
    );
    return await res.json();
  }, 'getStudyNotes', { userId, pathId, topicId });
}

/**
 * Create a study note
 */
export async function createStudyNote(userId, payload) {
  return safeApiCall(async () => {
    const res = await httpPost(`/api/users/${encodeURIComponent(userId)}/study-notes`, payload);
    return await res.json();
  }, 'createStudyNote', { userId, payload });
}

/**
 * Update a study note
 */
export async function updateStudyNote(userId, noteId, payload) {
  return safeApiCall(async () => {
    const res = await httpPut(`/api/users/${encodeURIComponent(userId)}/study-notes/${encodeURIComponent(noteId)}`, payload);
    return await res.json();
  }, 'updateStudyNote', { userId, noteId, payload });
}

/**
 * Delete a study note
 */
export async function deleteStudyNote(userId, noteId) {
  return safeApiCall(async () => {
    const res = await httpDelete(`/api/users/${encodeURIComponent(userId)}/study-notes/${encodeURIComponent(noteId)}`);
    return await res.json();
  }, 'deleteStudyNote', { userId, noteId });
}

export default {
  getUserStudyTracks,
  getStudyTrack,
  createStudyTrack,
  updateStudyTrack,
  deleteStudyTrack,
  getTrackProgress,
  markTopicComplete,
  getStudyNotes,
  createStudyNote,
  updateStudyNote,
  deleteStudyNote
};
